// Utility for enforcing minus sign in input fields based on badge value

/**
 * Enforces minus sign for input fields if badge value is negative.
 * badgeValue can be previousGold, previousSilver, or previousBalance depending on the context:
 * - Metal to Amount: use previousGold/previousSilver for gold/silver fields
 * - Amount to Metal: use previousBalance for amount, gold, and silver fields
 * @param {string|number} badgeValue - The badge value (gold/silver/balance)
 * @param {string} inputValue - The current input value
 * @returns {string} - The validated input value (with enforced minus if needed)
 */
export function enforceMinusIfNegative(badgeValue, inputValue) {
  const badgeNum = parseFloat(badgeValue);
  let sanitized = inputValue.replace(/[^\d.]/g, ""); // Remove all except digits and dot
  if (badgeNum < 0) {
    // Always enforce minus sign at the start, never allow positive or no sign
    if (sanitized === "") return "-"; // If empty, keep just minus
    return "-" + sanitized;
  } else {
    // Always remove minus sign for positive/zero badge
    return sanitized;
  }
}

/**
 * Checks if the input is valid for the field, preventing user from removing minus if badge is negative.
 * @param {string|number} badgeValue - The badge value (gold/silver/balance)
 * @param {string} inputValue - The value user is trying to set
 * @returns {boolean} - True if valid, false if not allowed
 */
export function isInputAllowed(badgeValue, inputValue) {
  const badgeNum = parseFloat(badgeValue);
  if (badgeNum < 0) {
    // Must start with minus, and not allow only minus (except for empty input)
    return inputValue === "-" || inputValue.startsWith("-");
  }
  // For zero or positive, any non-negative number is allowed
  return !inputValue.startsWith("-");
}

/**
 * Checks if the absolute value of input is less than or equal to the absolute value of badge value.
 * @param {string|number} badgeValue - The badge value (gold/silver)
 * @param {string|number} inputValue - The input value
 * @returns {boolean} - True if within allowed, false if not
 */
export function isWithinMaxAllowed(badgeValue, inputValue) {
  const badgeNum = Math.abs(parseFloat(badgeValue) || 0);
  const inputNum = Math.abs(parseFloat(inputValue) || 0);
  return inputNum <= badgeNum;
}

/**
 * Returns a validation message if inputValue is greater than badgeValue (for left-side validation).
 * @param {string|number} badgeValue - The badge value (previousGold/previousSilver)
 * @param {string|number} inputValue - The input value (updatedGold/updatedSilver)
 * @param {string} label - The label for the field (e.g., 'Gold', 'Silver')
 * @returns {string} - Validation message or empty string
 */
export function getMaxAllowedValidationMessage(badgeValue, inputValue, label) {
  const badgeNum = parseFloat(badgeValue) || 0;
  const inputNum = parseFloat(inputValue) || 0;
  // For negative badge, input should not be less (more negative) than badge
  if (badgeNum < 0) {
    if (inputNum < badgeNum) {
      return `Max allowed: ${badgeValue}`;
    }
  } else {
    // For positive badge, input should not be more than badge
    if (inputNum > badgeNum) {
      return `Max allowed: ${badgeValue}`;
    }
  }
  return "";
}

/**
 * Returns a validation message if the input amount is greater than the allowed balance (for amount-to-metal validation).
 * @param {string|number} balanceValue - The previous balance value
 * @param {string|number} inputAmount - The input amount value
 * @param {string} label - The label for the field (e.g., 'Amount')
 * @returns {string} - Validation message or empty string
 */
export function getMaxAllowedAmountValidationMessage(
  balanceValue,
  goldAmount,
  silverAmount,
  label = "Amount"
) {
  const balanceNum = parseFloat(balanceValue) || 0;
  const goldNum = parseFloat(goldAmount) || 0;
  const silverNum = parseFloat(silverAmount) || 0;
  const total = goldNum + silverNum;
  // For negative balance, total should not be less (more negative) than balance
  if (balanceNum < 0) {
    if (total < balanceNum) {
      return `Max allowed (inc. both field total): ${balanceValue}`;
    }
  } else {
    // For positive balance, total should not be more than balance
    if (total > balanceNum) {
      return `Max allowed (both field total): ${balanceValue}`;
    }
  }
  return "";
}
